"use client";

import { Card } from "../ui/Card";
import { SellItem } from "../../data/types";
import { INSTRUMENT_CATEGORIES } from "../../data/constants/index";
import {
  MapPin,
  Calendar,
  Brain,
  Clock,
  Truck,
  Package,
  Shield,
  Piano,
  Guitar,
  Music,
  Drum,
  Headphones,
  Music2,
  Heart,
  MessageCircle,
} from "lucide-react";
import { WatermarkImage } from "../ui/WatermarkImage";
import { Button } from "../ui/Button";
import { useRouter } from "next/navigation";
import { useAuth } from "../../lib/hooks/useAuth";
import { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { getFirebaseDb } from "../../lib/api/firebase-ultra-safe";
import { generateProductImageAlt } from "../../lib/seo/generateImageAlt";

// 찜하기 숫자를 계산하는 함수
const getWishlistCount = (itemId: string): number => {
  if (typeof window === "undefined") return 0;

  let totalCount = 0;

  // 모든 사용자의 찜하기 데이터를 확인
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith("wishlist_")) {
      try {
        const wishlistData = localStorage.getItem(key);
        if (wishlistData) {
          const wishlistIds = JSON.parse(wishlistData);
          if (Array.isArray(wishlistIds) && wishlistIds.includes(itemId)) {
            totalCount++;
          }
        }
      } catch (error) {
        console.error("찜하기 데이터 파싱 오류:", error);
      }
    }
  }

  return totalCount;
};

interface ItemCardProps {
  item: SellItem;
  onClick?: (item: SellItem) => void;
  isTradingTab?: boolean;
  currentUserId?: string;
  buyerUid?: string;
}

export function ItemCard({
  item,
  onClick,
  isTradingTab = false,
  currentUserId,
  buyerUid,
}: ItemCardProps) {
  const router = useRouter();
  const { user } = useAuth();
  const [chatCount, setChatCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  // 찜하기 카운트 및 상태 로드
  useEffect(() => {
    if (item.id) {
      const count = getWishlistCount(item.id);
      setWishlistCount(count);

      // 현재 사용자의 찜하기 상태 확인
      if (user?.uid) {
        const wishlistKey = `wishlist_${user.uid}`;
        const existingWishlist = JSON.parse(
          localStorage.getItem(wishlistKey) || "[]"
        );
        setIsLiked(existingWishlist.includes(item.id));
      }
    }
  }, [item.id, user?.uid]);

  // 채팅 개수 로드
  useEffect(() => {
    const loadChatCount = async () => {
      if (!item.id) return;
      
      try {
        const db = await getFirebaseDb();
        if (!db) return;

        const chatsRef = collection(db, "chats");
        const q = query(chatsRef, where("itemId", "==", item.id));
        const snapshot = await getDocs(q);
        setChatCount(snapshot.docs.length);
      } catch (error) {
        console.error("채팅 개수 로드 실패:", error);
      }
    };

    loadChatCount();
  }, [item.id]);

  const handleClick = async (hasTriedFix = false) => {
    console.log("🔍 ItemCard handleClick:", {
      itemId: item.id,
      itemStatus: item.status,
      currentUserId,
      buyerUid,
      itemBuyerUid: item.buyerUid,
      itemSellerUid: item.sellerUid,
      isBuyer: currentUserId && currentUserId === item.buyerUid,
      isSeller: currentUserId && currentUserId === item.sellerUid,
      hasTriedFix,
    });

    if (item.status === "sold") {
      alert("거래완료된 상품입니다.");
      return;
    }

    if (item.status === "reserved" || item.status === "escrow_completed") {
      const isSeller = currentUserId && currentUserId === item.sellerUid;
      const isBuyer = currentUserId && currentUserId === item.buyerUid;

      if (!isSeller && !isBuyer) {
        if (item.buyerUid === null && !hasTriedFix) {
          try {
            const debugResponse = await fetch(
              `/api/debug-item?itemId=${item.id}`
            );
            const debugResult = await debugResponse.json();

            if (debugResult.success) {
              if (
                debugResult.data.item.buyerId &&
                !debugResult.data.item.buyerUid
              ) {
                const { fixBuyerUid } = await import(
                  "../../lib/api/fix-buyer-uid"
                );
                const fixResult = await fixBuyerUid(item.id);

                if (fixResult.success && fixResult.data.buyerUid) {
                  item.buyerUid = fixResult.data.buyerUid;
                  return handleClick(true);
                }
              }

              const firstTransaction =
                debugResult.data.summary.firstTransaction;
              if (firstTransaction && firstTransaction.buyerId) {
                const { fixBuyerUid } = await import(
                  "../../lib/api/fix-buyer-uid"
                );
                const fixResult = await fixBuyerUid(item.id);

                if (fixResult.success && fixResult.data.buyerUid) {
                  item.buyerUid = fixResult.data.buyerUid;
                  return handleClick(true);
                }
              }
            }
          } catch (error) {
            console.error("❌ 디버깅 중 오류:", error);
          }
        }

        const isSeller = user?.uid === item.sellerUid;
        const isBuyer = user?.uid === item.buyerUid;

        if (!isSeller && !isBuyer) {
          alert("거래중인 상품은 판매자와 구매자만 볼 수 있습니다.");
          return;
        }
      }
    }

    if (onClick) {
      onClick(item);
      return;
    }

    router.push(`/item/${item.id}`);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("ko-KR").format(price) + "원";
  };

  const formatDate = (date: any) => {
    if (!date) return "";

    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(dateObj.getTime())) return "";

      const now = new Date();
      const diffInMs = now.getTime() - dateObj.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

      if (diffInMinutes < 1) return "방금 전";
      else if (diffInMinutes < 60) return `${diffInMinutes}분 전`;
      else if (diffInHours < 24) return `${diffInHours}시간 전`;
      else if (diffInDays < 7) return `${diffInDays}일 전`;
      else return dateObj.toLocaleDateString("ko-KR");
    } catch (error) {
      return "";
    }
  };

  const getCategoryIcon = (category: string): React.ReactNode => {
    const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
      "건반": Piano,
      "현악": Guitar,
      "관악": Music,
      "타악": Drum,
      "국악": Music2,
      "음향": Headphones,
      "특수": Music2,
      "용품": Package,
    };
    const IconComponent = iconMap[category] || Music;
    return <IconComponent className="w-3.5 h-3.5 text-[#7A56E8]" strokeWidth={2} />;
  };

  const getCategoryLabel = (category: string) => {
    const categoryInfo = INSTRUMENT_CATEGORIES.find(c => c.key === category);
    return categoryInfo?.label || category;
  };

  const getShippingTypeLabel = (type: string) => {
    switch (type) {
      case "direct":
        return "직거래";
      case "pickup":
        return "픽업";
      case "courier":
      case "parcel":
        return "택배";
      case "meetup":
        return "직거래";
      case "escrow":
        return "안전거래";
      case "shipping":
        return "화물운송";
      default:
        return type;
    }
  };

  const isSold = item.status === "sold";
  const isReserved =
    (item.status === "reserved" || item.status === "escrow_completed") &&
    currentUserId &&
    currentUserId !== item.sellerUid &&
    currentUserId !== item.buyerUid;

  return (
    <article
      className={`bg-white rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg border border-neutral-100 ${
        isSold ? "opacity-60" : ""
      }`}
      onClick={
        isSold
          ? undefined
          : e => {
              const target = e.target as HTMLElement;
              if (target.closest("button") || target.closest("a")) {
                return;
              }
              handleClick();
            }
      }
      aria-label={`${item.brand} ${item.model} - ${formatPrice(item.price)}`}
    >
      {/* 이미지 - Spacing: rounded-2xl (16px), top-2 (8px) */}
      <div className="aspect-square bg-neutral-100 relative overflow-hidden rounded-t-2xl">
        {item.images && item.images.length > 0 ? (
          <WatermarkImage
            src={item.images[0]}
            alt={generateProductImageAlt(item, 0)}
            className="w-full h-full object-cover"
            isAiProcessed={
              item.aiProcessedImages?.some(aiImg => aiImg.imageIndex === 0) ||
              false
            }
            showWatermark={true}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
            {(item as any).tradeType === "buy" ? (
              <div className="text-center px-4">
                <div className="p-4 rounded-xl bg-white/60 border border-primary-200 mb-2">
                  <Brain className="w-8 h-8 text-primary-600" strokeWidth={2} aria-hidden="true" />
                </div>
                <div className="text-base font-bold text-neutral-700">
                  구매합니다
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full p-4">
                <div className="p-6 rounded-xl bg-white/60 border border-primary-200">
                  {(() => {
                    const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
                      "건반": Piano,
                      "현악": Guitar,
                      "관악": Music,
                      "타악": Drum,
                      "국악": Music2,
                      "음향": Headphones,
                      "특수": Music2,
                      "용품": Package,
                    };
                    const IconComponent = iconMap[item.category] || Music;
                    return <IconComponent className="w-12 h-12 text-primary-600" strokeWidth={2} aria-hidden="true" />;
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI 배지 - Spacing: top-2 left-2 (8px), px-2 py-1 (8px/4px) */}
        {item.aiProcessedImages && item.aiProcessedImages.length > 0 && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-primary-600 to-primary-400 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1 shadow-md" aria-label="AI 분석 완료">
            <Brain className="w-3 h-3" aria-hidden="true" />
            <span>AI</span>
          </div>
        )}

        {/* 거래 상태 배지 - Spacing: top-2 right-2 (8px) */}
        {(item.status === "reserved" ||
          item.status === "escrow_completed" ||
          item.status === "shipping") && (
          <div className="absolute top-2 right-2 bg-orange-500/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-bold" aria-label="거래중">
            거래중
          </div>
        )}

        {item.status === "sold" && (
          <div className="absolute top-2 right-2 bg-neutral-700/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs font-bold" aria-label="거래완료">
            거래완료
          </div>
        )}
      </div>

      {/* 상품 정보 - 패딩 16px 고정 */}
      <div className="p-4">
        {/* 제목 - Typography: text-base (16px) */}
        <h3 className="text-base font-bold text-neutral-900 mb-2 line-clamp-2 min-h-[3rem]">
          {item.title || `${item.brand} ${item.model}`}
        </h3>

        {/* 가격 - Typography: text-xl (20px) */}
        <div className="text-xl font-bold text-primary-600 mb-3">
          {formatPrice(item.price)}
        </div>

        {/* 지역/등록일 - Typography: text-xs (12px), Spacing: gap-2 (8px) */}
        <div className="flex items-center gap-2 mb-3 overflow-hidden">
          <span className="flex items-center gap-1 text-xs text-neutral-500 flex-shrink-0" aria-label={`지역: ${item.region || "지역정보 없음"}`}>
            <MapPin className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
            <span className="truncate max-w-[100px] whitespace-nowrap">
              {item.region ? item.region.replace(/\s*>\s*/g, " ") : "지역정보 없음"}
            </span>
          </span>
          <span className="flex items-center gap-1 text-xs text-neutral-500 whitespace-nowrap flex-shrink-0" aria-label={`등록일: ${formatDate(item.createdAt)}`}>
            <Calendar className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
            <span>{formatDate(item.createdAt)}</span>
          </span>
        </div>

        {/* 카테고리 - 첫 번째 줄, Spacing: gap-2 (8px), mb-2 (8px) */}
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-50 text-primary-600 rounded-xl text-xs font-medium whitespace-nowrap flex-shrink-0">
            {getCategoryIcon(item.category)}
            {getCategoryLabel(item.category)}
          </span>
        </div>

        {/* 거래방식 및 버튼 - 두 번째 줄, Spacing: gap-2 (8px) */}
        <div className="flex items-center justify-between gap-2">
          {/* 왼쪽: 거래방식 */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* 배송 방식 칩 */}
            {item.shippingTypes && item.shippingTypes.length > 0 && (
              <>
                {item.shippingTypes.map((type, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-neutral-100 text-neutral-700 rounded-xl text-xs font-medium whitespace-nowrap flex-shrink-0"
                  >
                    {getShippingTypeLabel(type)}
                  </span>
                ))}
              </>
            )}

            {/* 안전결제 칩 */}
            {(item as any).escrowEnabled && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-xl text-xs font-medium whitespace-nowrap flex-shrink-0">
                <Shield className="w-3 h-3" aria-hidden="true" />
                안전결제
              </span>
            )}
          </div>

          {/* 오른쪽: 찜하기/메시지 카운트 - Spacing: gap-0.5 (4px) */}
          <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto pl-6">
            {/* 찜하기 카운트 */}
            {wishlistCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!user?.uid) {
                    alert("로그인이 필요합니다.");
                    return;
                  }
                  
                  const newLikedState = !isLiked;
                  setIsLiked(newLikedState);
                  
                  const wishlistKey = `wishlist_${user.uid}`;
                  const existingWishlist = JSON.parse(
                    localStorage.getItem(wishlistKey) || "[]"
                  );
                  
                  if (newLikedState) {
                    if (!existingWishlist.includes(item.id)) {
                      existingWishlist.push(item.id);
                      localStorage.setItem(
                        wishlistKey,
                        JSON.stringify(existingWishlist)
                      );
                      setWishlistCount(prev => prev + 1);
                    }
                  } else {
                    const updatedWishlist = existingWishlist.filter(
                      (id: string) => id !== item.id
                    );
                    localStorage.setItem(
                      wishlistKey,
                      JSON.stringify(updatedWishlist)
                    );
                    setWishlistCount(prev => Math.max(0, prev - 1));
                  }
                }}
                className="flex items-center gap-0.5 text-neutral-600 hover:text-red-500 transition-colors whitespace-nowrap"
                aria-label={`찜하기 ${wishlistCount}개`}
                aria-pressed={isLiked}
              >
                <Heart
                  className={`w-3.5 h-3.5 ${
                    isLiked ? "fill-current text-red-500" : ""
                  }`}
                  aria-hidden="true"
                />
                <span className="text-xs">{wishlistCount}</span>
              </button>
            )}

            {/* 메시지 카운트 */}
            {chatCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onClick) {
                    onClick(item);
                  }
                }}
                className="flex items-center gap-0.5 text-neutral-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                aria-label={`채팅 ${chatCount}개`}
              >
                <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="text-xs">{chatCount}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
